import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAlert } from "@/hooks/useAlert";
import { createMagicLogin } from "@/services/auth";
import { AlertTypes } from "@/utils/atoms";
import { fetchPublicAccessToken } from "@/utils/fetchPublicAccessToken";
import {
  classifyMagicLinkRequestError,
  getMagicLinkRequestErrorKey,
  type MagicLinkRequestError,
} from "@/utils/magicLinkErrors";

export type {
  MagicLinkConsumeError,
  MagicLinkRequestError,
} from "@/utils/magicLinkErrors";

export const useMagicLinkLogin = () => {
  const { t } = useTranslation();
  const showAlert = useAlert();
  const [isRequesting, setIsRequesting] = useState(false);

  const requestMagicLink = async (
    email: string,
  ): Promise<{ ok: true } | { ok: false; reason: MagicLinkRequestError }> => {
    setIsRequesting(true);

    try {
      const publicToken = await fetchPublicAccessToken();

      await createMagicLogin(
        { email },
        { headers: { Authorization: `Bearer ${publicToken}` } },
      );

      return { ok: true };
    } catch (error) {
      return {
        ok: false,
        reason: classifyMagicLinkRequestError(error),
      };
    } finally {
      setIsRequesting(false);
    }
  };

  const showRequestError = (reason: MagicLinkRequestError) => {
    showAlert({
      type: AlertTypes.error,
      text: t(getMagicLinkRequestErrorKey(reason)),
    });
  };

  return {
    requestMagicLink,
    showRequestError,
    isRequesting,
  };
};
